import {
  DEFAULT_MMS_PRICE_IN_TENTH_CENTS,
  DEFAULT_MONTHLY_MINIMUM_IN_TENTH_CENTS,
  DEFAULT_SMS_PRICE_IN_TENTH_CENTS,
} from "@redotech/redo-model/support/billing/text-message-billing";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import { memo } from "react";

export const phonesBillingTextMessagingForm = groupInput({
  pricePerSMSInTenthCents: input<string>(),
  pricePerMMSInTenthCents: input<string>(),
  monthlyMinimumInTenthCents: input<string>(),
});

export const phonesBillingTextMessagingDefault: PhonesBillingTextMessagingValue =
  {
    pricePerSMSInTenthCents: DEFAULT_SMS_PRICE_IN_TENTH_CENTS.toString(),
    pricePerMMSInTenthCents: DEFAULT_MMS_PRICE_IN_TENTH_CENTS.toString(),
    monthlyMinimumInTenthCents:
      DEFAULT_MONTHLY_MINIMUM_IN_TENTH_CENTS.toString(),
  };

export type PhonesBillingTextMessagingForm = InputSchema.Input<
  typeof phonesBillingTextMessagingForm
>;

export type PhonesBillingTextMessagingValue = InputSchema.Value<
  typeof phonesBillingTextMessagingForm
>;

export const phonesBillingVoiceForm = groupInput({
  pricePerMinuteInCents: input<string>(),
  pricePerMonthInCents: input<string>(),
});

export const phonesBillingVoiceDefault: PhonesBillingVoiceValue = {
  pricePerMinuteInCents: "3",
  pricePerMonthInCents: "1000",
};

export type PhonesBillingVoiceForm = InputSchema.Input<
  typeof phonesBillingVoiceForm
>;

export type PhonesBillingVoiceValue = InputSchema.Value<
  typeof phonesBillingVoiceForm
>;

export const PhonesBillingCard = memo(function PhonesBillingCard({
  textMessagingInput,
  voiceInput,
}: {
  textMessagingInput: PhonesBillingTextMessagingForm;
  voiceInput: PhonesBillingVoiceForm;
}) {
  const textInputs = textMessagingInput.inputs;
  const voiceInputs = voiceInput.inputs;

  return (
    <Flex dir="column" gap="xl">
      <Card radius="sm" title="Text Messaging Billing">
        <Flex dir="column" gap="lg">
          <RedoTextInput
            description="If the merchant has already been billed for the month, the new price will be charged the following month."
            IconLeading={() => <span>$</span>}
            inputTypeHint="number"
            label="Base price per month"
            min={0}
            setValue={textInputs.monthlyMinimumInTenthCents.setValue}
            size="sm"
            step={0.001}
            value={textInputs.monthlyMinimumInTenthCents.value}
          />
          <RedoTextInput
            IconLeading={() => <span>$</span>}
            inputTypeHint="number"
            label="Price per MMS message (inbound + outbound)"
            min={0}
            setValue={textInputs.pricePerMMSInTenthCents.setValue}
            size="sm"
            step={0.001}
            value={textInputs.pricePerMMSInTenthCents.value}
          />
          <RedoTextInput
            IconLeading={() => <span>$</span>}
            inputTypeHint="number"
            label="Price per SMS message (inbound + outbound)"
            min={0}
            setValue={textInputs.pricePerSMSInTenthCents.setValue}
            size="sm"
            step={0.001}
            value={textInputs.pricePerSMSInTenthCents.value}
          />
        </Flex>
      </Card>

      <Card radius="sm" title="Voice Billing">
        <Flex dir="column" gap="lg">
          <RedoTextInput
            description="Base monthly subscription fee for voice service"
            IconLeading={() => <span>$</span>}
            inputTypeHint="number"
            label="Base price per month"
            min={0}
            setValue={voiceInputs.pricePerMonthInCents.setValue}
            size="sm"
            step={0.01}
            value={voiceInputs.pricePerMonthInCents.value}
          />
          <RedoTextInput
            IconLeading={() => <span>$</span>}
            inputTypeHint="number"
            label="Price per minute"
            min={0}
            setValue={voiceInputs.pricePerMinuteInCents.setValue}
            size="sm"
            step={0.01}
            value={voiceInputs.pricePerMinuteInCents.value}
          />
        </Flex>
      </Card>
    </Flex>
  );
});
